import { commFournierTemp } from '@/1001repas/comm/comm-fournier-temp';
import { ORIGINS_ICONS, ORIGINS_ICONS_SCOLLER } from '@/1001repas/origins/image';
import { initDishesRotation } from '@/fjm/_site/makers/dishes-rotation.maker';
import AppConstants from '@/shared/app/model/AppConstants';
import PRICE_FORMAT from '@/shared/app/model/PriceFormat';
import Site2 from '@/shared/app/model/templating/Site2';
import { DishesMock } from '@/shared/data/mocks/dishes/dishes.mock';
import { notInProduction } from '@/shared/data/mocks/not-in-production';
import { weeklyPick } from '@/shared/data/weekly/weekly-pick';
import { readActiveDishes } from '@/shared/data/whats-active/active-dishes';
import { makeUniCommunications } from '@/shared/uni/templating/components/communications/ui/maker';
import {
  makeUniAccueil,
  MakeUniAccueilOptions,
} from '@/shared/uni/templating/makers/accueil.maker';
import { getMilleEtUnTheme } from '../1_fournier_mobalpa_thones/theme';

// 290 - GEG Grenoble

const mock = new DishesMock({
  origins: ORIGINS_ICONS,
  maxDishes: 4,
  minDishes: 1,
  maxOrigins: 3,
  sides: true,
});
const mock1 = new DishesMock({
  maxDishes: 1,
  maxAllergens: 0,
  maxOrigins: 0,
  sides: false,
});

const GegGrenoble: Site2 = {
  pages: [
    // [4000] Accueil offre vertical - rotation CSS
    {
      templateGetter: (state, getters) => {
        const theme1 = getMilleEtUnTheme(getters.value('dishes1', 'theme')?.toString());
        const theme2 = getMilleEtUnTheme(getters.value('dishes2', 'theme')?.toString());
        const theme3 = getMilleEtUnTheme(getters.value('dishes3', 'theme')?.toString());

        // Seuls les stands avec au moins 1 plat actif sont affichés
        const activeStandsIndexes = readActiveDishes(getters, ['dishes1', 'dishes2', 'dishes3']);
        // Récupération accompagnements
        let sides = '';
        Object.entries(activeStandsIndexes).forEach(([dataset]) => {
          const values = getters.all(dataset);
          if (values.sides) sides += `${sides.length ? ' — ' : ''}${values.sides}`;
        });
        if (process.env !== AppConstants.ENV.PRODUCTION) sides = sides.substring(0, 85);

        const stands: MakeUniAccueilOptions['stands'] = [
          {
            headerImage: theme1.headerImage,
            datasetName: 'dishes1',
          },
          {
            headerImage: theme2.headerImage,
            datasetName: 'dishes2',
          },
          {
            headerImage: theme3.headerImage,
            datasetName: 'dishes3',
          },
        ];

        const accueil = {
          component: 'AutoFlex',
          config: {
            vertical: true,
            grow: '0',
            basis: '0',
            offreGrow: '1',
            ttcBasis: '3vh',
            legumesBasis: '3.5vh',
            commBasis: '56.25vh',
          },
          slots: {
            offre: makeUniAccueil({
              backgroundColor: '#f2f2f2',
              originsIcons: ORIGINS_ICONS_SCOLLER,
              stands,
              keepStandsWithNoActiveDishes: false,
              getters,
              sizeFactor: 0.92,
              dishConfig: {
                priceTextColor: 'black',
                priceBackgroundColor: '#F7F7F7',
                lowerSizeFactor: '1.2',
                centerContentInUpper: true,
              },
              dishesGroupConfig: {
                numberOfDishesPerPage: 2,
                numberOfDishesPerRow: 2,
                minNumberOfDishesPerRow: 1,
                pagesTransition: 'slideleft',
                borderRadius: '.5rem',
              },
            }),
            ...(sides
              ? {
                  legumes: {
                    component: 'TText',
                    config: {
                      text: sides,
                      color: '#858D9F',
                      padding: '0rem 1.5rem',
                      backgroundColor: '#f2f2f2',
                      textAlign: 'center',
                      fontSize: '1.8rem',
                    },
                  },
                }
              : {}),
            ttc: {
              component: 'TText',
              config: {
                text: 'Tous nos prix sont exprimés TTC',
                color: '#858D9F',
                padding: '0rem 1.5rem',
                backgroundColor: '#f2f2f2',
                textAlign: 'center',
                fontSize: '1.6rem',
              },
            },
            comm: makeUniCommunications({
              mask: {
                backgroundImage: '/storage/comm/fournier_welcome.webp',
              },
            }),
          },
        };

        return {
          component: 'Alternate',
          config: {
            transition: 'slide-left',
            accueilDuration: '15000',
            duration: '7000',
          },
          slots: {
            accueil,
          },
        };
      },
      datasets: {
        dishes1: weeklyPick('geg_grenoble_bistrot'),
        dishes2: weeklyPick('geg_grenoble_plancha'),
        dishes3: weeklyPick('geg_grenoble_etaldumarche'),
        communications: 'communications_site_6746bc52c0e9bf00123f80de',
      },
      default: {
        dishes1: {
          ...notInProduction(mock.make()),
          theme: 'bistrot',
        },
        dishes2: {
          ...notInProduction(mock.make()),
          theme: 'plancha',
        },
        dishes3: {
          ...notInProduction(mock.make()),
          theme: 'etaldumarche',
        },
        [AppConstants.APP_DATASET_NAME]: {
          rotate: AppConstants.ROTATE.PORTRAIT,
          priceFormat: PRICE_FORMAT.EURO_SPACE,
        },
      },
      init() {
        initDishesRotation();
      },
    },
    // [4001] Accueil offre horizontal
    {
      templateGetter: (state, getters) => {
        const communications = commFournierTemp('geg_grenoble');
        const standsLeft = [
          {
            datasetName: 'dishes1',
            headerImage: getMilleEtUnTheme(getters.value('dishes1', 'theme')?.toString())
              .headerImage,
          },
          {
            datasetName: 'dishes2',
            headerImage: getMilleEtUnTheme(getters.value('dishes2', 'theme')?.toString())
              .headerImage,
          },
        ];

        const standsRight = [
          {
            datasetName: 'dishes3',
            headerImage: getMilleEtUnTheme(getters.value('dishes3', 'theme')?.toString())
              .headerImage,
          },
        ];

        return {
          component: 'AutoFlex',
          config: {
            grow: '1',
            basis: '0',
            shrink: '0',
            commBasis: '56.25vh',
          },
          slots: {
            ext: {
              component: 'AutoFlex',
              config: {
                grow: '0',
                basis: '50%',
              },
              slots: {
                left: makeUniAccueil({
                  backgroundColor: '#f2f2f2',
                  originsIcons: ORIGINS_ICONS,
                  stands: standsLeft,
                  minNumberOfStandsInFlex: 2,
                  keepStandsWithNoActiveDishes: false,
                  getters,
                  sizeFactor: 1,
                  dishConfig: {
                    priceTextColor: 'black',
                    priceNoBackground: true,
                    lowerSizeFactor: '1.2',
                    centerContentInUpper: true,
                  },
                  dishesGroupConfig: {
                    numberOfDishesPerPage: 1,
                    showIndicator: true,
                    pagesTransition: 'slideleft',
                    borderRadius: '.5rem',
                    indicatorThickness: '.15rem',
                  },
                }),
                right: {
                  component: 'AutoFlex',
                  config: {
                    vertical: true,
                    grow: '1',
                    basis: '0',
                  },
                  slots: {
                    top: makeUniAccueil({
                      backgroundColor: '#f2f2f2',
                      originsIcons: ORIGINS_ICONS,
                      stands: standsRight,
                      minNumberOfStandsInFlex: 1,
                      keepStandsWithNoActiveDishes: false,
                      boxPadding: '1.4em 1.4em 1.4em 0',
                      getters,
                      sizeFactor: 1,
                      dishConfig: {
                        priceTextColor: 'black',
                        priceNoBackground: true,
                        lowerSizeFactor: '1.2',
                        centerContentInUpper: true,
                      },
                      dishesGroupConfig: {
                        numberOfDishesPerPage: 1,
                        showIndicator: true,
                        pagesTransition: 'slideleft',
                        borderRadius: '.5rem',
                        indicatorThickness: '.15rem',
                      },
                    }),
                    bottom: {
                      component: 'Box',
                      config: {},
                      slots: {
                        ext: makeUniCommunications({
                          mask: {
                            backgroundImage: '/storage/comm/fournier_welcome.webp',
                          },
                        }),
                      },
                    },
                  },
                },
              },
            },
          },
        };
      },
      datasets: {
        dishes1: weeklyPick('geg_grenoble_bistrot'),
        dishes2: weeklyPick('geg_grenoble_plancha'),
        dishes3: weeklyPick('geg_grenoble_etaldumarche'),
        communications: 'communications_site_6746bc52c0e9bf00123f80de',
      },
      default: {
        dishes1: {
          ...notInProduction({
            ...mock.make(),
          }),
          theme: 'bistrot',
        },
        dishes2: {
          ...notInProduction({
            ...mock.make(),
          }),
          theme: 'plancha',
        },
        dishes3: {
          ...notInProduction({
            ...mock.make(),
          }),
          theme: 'etaldumarche',
        },
      },
      init() {
        initDishesRotation();
      },
    },
    // [4001] Accueil offre vertical - pour aperçu
    {
      templateGetter: (state, getters) => {
        const comm = commFournierTemp('geg_grenoble');
        const theme1 = getMilleEtUnTheme(getters.value('dishes1', 'theme')?.toString());
        const theme2 = getMilleEtUnTheme(getters.value('dishes2', 'theme')?.toString());
        const theme3 = getMilleEtUnTheme(getters.value('dishes3', 'theme')?.toString());

        // Seuls les stands avec au moins 1 plat actif sont affichés
        const activeStandsIndexes = readActiveDishes(getters, ['dishes1', 'dishes2', 'dishes3']);
        // Récupération accompagnements
        let sides = '';
        Object.entries(activeStandsIndexes).forEach(([dataset]) => {
          const values = getters.all(dataset);
          if (values.sides) sides += `${sides.length ? ' — ' : ''}${values.sides}`;
        });
        if (process.env !== AppConstants.ENV.PRODUCTION) sides = sides.substring(0, 85);

        const stands: MakeUniAccueilOptions['stands'] = [
          {
            headerImage: theme1.headerImage,
            datasetName: 'dishes1',
          },
          {
            headerImage: theme2.headerImage,
            datasetName: 'dishes2',
          },
          {
            headerImage: theme3.headerImage,
            datasetName: 'dishes3',
          },
        ];

        const accueil = {
          component: 'AutoFlex',
          config: {
            vertical: true,
            grow: '0',
            basis: '0',
            offreGrow: '1',
            ttcBasis: '3vw',
            legumesBasis: '3.5vw',
            commBasis: '56.25vw',
          },
          slots: {
            offre: makeUniAccueil({
              backgroundColor: '#f2f2f2',
              originsIcons: ORIGINS_ICONS_SCOLLER,
              stands,
              keepStandsWithNoActiveDishes: false,
              getters,
              sizeFactor: 0.92,
              dishConfig: {
                priceTextColor: 'black',
                priceBackgroundColor: '#F7F7F7',
                lowerSizeFactor: '1.2',
                centerContentInUpper: true,
              },
              dishesGroupConfig: {
                numberOfDishesPerPage: 2,
                numberOfDishesPerRow: 2,
                minNumberOfDishesPerRow: 1,
                pagesTransition: 'slideleft',
                borderRadius: '.5rem',
              },
            }),
            ...(sides
              ? {
                  legumes: {
                    component: 'TText',
                    config: {
                      text: sides,
                      color: '#858D9F',
                      padding: '0rem 1.5rem',
                      backgroundColor: '#f2f2f2',
                      textAlign: 'center',
                      fontSize: '1.8rem',
                    },
                  },
                }
              : {}),
            ttc: {
              component: 'TText',
              config: {
                text: 'Tous nos prix sont exprimés TTC',
                color: '#858D9F',
                padding: '0rem 1.5rem',
                backgroundColor: '#f2f2f2',
                textAlign: 'center',
                fontSize: '1.6rem',
              },
            },
            comm: makeUniCommunications({
              mask: {
                backgroundImage: '/storage/comm/fournier_welcome.webp',
              },
            }),
          },
        };

        return {
          component: 'Alternate',
          config: {
            transition: 'slide-left',
            accueilDuration: '15000',
            duration: '7000',
          },
          slots: {
            accueil,
          },
        };
      },
      datasets: {
        dishes1: weeklyPick('geg_grenoble_bistrot'),
        dishes2: weeklyPick('geg_grenoble_plancha'),
        dishes3: weeklyPick('geg_grenoble_etaldumarche'),
        communications: 'communications_site_6746bc52c0e9bf00123f80de',
      },
      default: {
        dishes1: {
          ...notInProduction(mock.make()),
          theme: 'bistrot',
        },
        dishes2: {
          ...notInProduction(mock.make()),
          theme: 'plancha',
        },
        dishes3: {
          ...notInProduction(mock.make()),
          theme: 'etaldumarche',
        },
      },
      init() {
        initDishesRotation();
      },
    },
  ],
};

export default GegGrenoble;
